import { differenceInCalendarDays, parseISO } from "date-fns";

export interface ClientStat {
  key: string;
  name: string;
  email: string;
  phone: string;
  locale: string;
  bookings: number;
  orders: number;
  bookingRevenue: number;
  orderRevenue: number;
  totalRevenue: number;
  avgTicket: number;
  firstDate: string | null;
  lastDate: string | null;
  daysSinceLast: number | null;
}

export interface Bucket {
  id: string;
  label: string;
  min: number;
  max: number;
}

export const REACTIVATION_BUCKETS: Bucket[] = [
  { id: "30-60", label: "30 a 60 dias", min: 30, max: 60 },
  { id: "61-90", label: "61 a 90 dias", min: 61, max: 90 },
  { id: "91-120", label: "91 a 120 dias", min: 91, max: 120 },
  { id: "121-150", label: "121 a 150 dias", min: 121, max: 150 },
  { id: "151-180", label: "151 a 180 dias", min: 151, max: 180 },
  { id: "181-210", label: "181 a 210 dias", min: 181, max: 210 },
  { id: "211-365", label: "211 a 365 dias", min: 211, max: 365 },
  { id: "365+", label: "Mais de 365 dias", min: 366, max: 100000 },
];

const REVENUE_STATUSES = ["confirmed", "completed", "concluded", "done"];

interface RawBooking {
  client_name?: string | null;
  client_email?: string | null;
  client_phone?: string | null;
  booking_date?: string | null;
  total_price?: number | null;
  status?: string | null;
  locale?: string | null;
}

interface RawOrder {
  client_name?: string | null;
  client_email?: string | null;
  client_phone?: string | null;
  created_at?: string | null;
  total_amount?: number | null;
  status?: string | null;
  locale?: string | null;
}

const cleanPhone = (p?: string | null) => String(p || "").replace(/[^0-9]/g, "");

const identity = (email?: string | null, phone?: string | null) => {
  const e = String(email || "").trim().toLowerCase();
  if (e) return `e:${e}`;
  const p = cleanPhone(phone);
  return p ? `p:${p.slice(-9)}` : "";
};

/** Agrega reservas e encomendas por cliente (chave: email, fallback telemóvel). */
export function buildClientStats(bookings: RawBooking[], orders: RawOrder[] = []): ClientStat[] {
  const map = new Map<string, ClientStat>();
  const today = new Date();

  const ensure = (key: string, name: string, email: string, phone: string, locale: string) => {
    let c = map.get(key);
    if (!c) {
      c = {
        key, name, email, phone, locale,
        bookings: 0, orders: 0,
        bookingRevenue: 0, orderRevenue: 0, totalRevenue: 0, avgTicket: 0,
        firstDate: null, lastDate: null, daysSinceLast: null,
      };
      map.set(key, c);
    }
    if (!c.name && name) c.name = name;
    if (!c.email && email) c.email = email;
    if (!c.phone && phone) c.phone = phone;
    return c;
  };

  const touchDate = (c: ClientStat, date: string | null) => {
    if (!date) return;
    const d = date.slice(0, 10);
    if (!c.firstDate || d < c.firstDate) c.firstDate = d;
    if (!c.lastDate || d > c.lastDate) c.lastDate = d;
  };

  for (const b of bookings) {
    if (b.status === "blocked" || b.status === "cancelled" || b.status === "rejected") continue;
    const key = identity(b.client_email, b.client_phone);
    if (!key) continue;
    const c = ensure(
      key,
      String(b.client_name || "").trim(),
      String(b.client_email || "").trim(),
      String(b.client_phone || "").trim(),
      String(b.locale || "pt-PT"),
    );
    c.bookings += 1;
    if (REVENUE_STATUSES.includes(String(b.status || ""))) {
      c.bookingRevenue += Number(b.total_price || 0);
    }
    touchDate(c, b.booking_date || null);
  }

  for (const o of orders) {
    if (o.status === "cancelled") continue;
    const key = identity(o.client_email, o.client_phone);
    if (!key) continue;
    const c = ensure(
      key,
      String(o.client_name || "").trim(),
      String(o.client_email || "").trim(),
      String(o.client_phone || "").trim(),
      String(o.locale || "pt-PT"),
    );
    c.orders += 1;
    c.orderRevenue += Number(o.total_amount || 0);
    touchDate(c, o.created_at ? o.created_at.slice(0, 10) : null);
  }

  const list = Array.from(map.values());
  for (const c of list) {
    c.totalRevenue = c.bookingRevenue + c.orderRevenue;
    const visits = c.bookings + c.orders;
    c.avgTicket = visits > 0 ? c.totalRevenue / visits : 0;
    if (c.lastDate) {
      const diff = differenceInCalendarDays(today, parseISO(c.lastDate));
      c.daysSinceLast = diff < 0 ? 0 : diff;
    }
  }
  return list;
}

export function bucketOf(days: number | null): Bucket | null {
  if (days === null) return null;
  return REACTIVATION_BUCKETS.find((b) => days >= b.min && days <= b.max) || null;
}

export function groupByBucket(clients: ClientStat[]): Record<string, ClientStat[]> {
  const out: Record<string, ClientStat[]> = {};
  for (const b of REACTIVATION_BUCKETS) out[b.id] = [];
  for (const c of clients) {
    const b = bucketOf(c.daysSinceLast);
    if (b) out[b.id].push(c);
  }
  for (const id of Object.keys(out)) {
    out[id].sort((a, b) => (b.daysSinceLast || 0) - (a.daysSinceLast || 0));
  }
  return out;
}
